import {
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/system'
import OfoqAutoComplete from '../../../../../components/Forms/OfoqAutoComplete.jsx'
import OfoqTextField from '../../../../../components/Forms/OfoqTextField.jsx'
import OfoqTextArea from '../../../../../components/Forms/OfoqTextArea.jsx'
import completeInfoImage from '../../../../../images/complete-profile-art.svg'
import OfoqCheckBox from '../../../../../components/Forms/OfoqCheckBox.jsx'
import OfoqPasswordField from '../../../../../components/Forms/OfoqPasswordField.jsx'
import { isNotEmpty, objNotEmpty } from '../../../../../utils/helpers'
import {
  getEmployeesWithPagination,
  searchEmployees,
} from '../../../../../redux/features/employees/employeesThunk'
import retrieveTitles from '../../../../../redux/features/titles/titlesThunk'
import retrieveDepartments from '../../../../../redux/features/departments/departmentsThunk'
import { selectTitles } from '../../../../../redux/features/titles/titlesSlice'
import { selectEmployees } from '../../../../../redux/features/employees/employeesSlice'
import { selectDepartments } from '../../../../../redux/features/departments/departmentsSlice'
import {
  retrieveSystemAdminUsersCompanies,
  updateSelectedSystemAdminUserStore,
  updateTenantID,
} from '../../../../../redux/features/users/SystemAdminUsers/SystemAdminUsersThunk'
import OfoqMultiAutoCompleteWithLoadMore from '../../../../../components/Forms/OfoqMultiAutoCompleteWithLoadMore.jsx'
import { ReactComponent as TeamSnap } from '../../../../../images/TeamSnap.svg'
import { SAML_KEY } from '../../../../../utils/constants'
import { selectSystemAdminUsers } from '../../../../../redux/features/users/SystemAdminUsers/SystemAdminUsersSlice'
import UseTranslation from '../../../../../hooks/UseTranslation.jsx'
import { ReactComponent as PaymentReminderIcon } from '../../../../../images/ring.svg'

const DEFAULT_SUBSCRIPTION_REMINDER = { reminder: false, en_message: '', ar_message: '' }

const UserStepOne = ({ setValidStepOne }) => {
  const { id } = useParams()
  const systemAdminUsers = useSelector(selectSystemAdminUsers)
  const { t } = UseTranslation()
  const titles = useSelector(selectTitles)
  const employees = useSelector(selectEmployees)
  const department = useSelector(selectDepartments)
  const dispatch = useDispatch()

  const [customer_admin, setCustomerAdmin] =
    useState(systemAdminUsers.updatedUser?.customer_admin || false)
  const [company, setCompany] = useState(systemAdminUsers.updatedUser?.company)
  const [integrationType, setIntegrationType] = useState(company?.tenant?.integration?.type)
  const [employee, setEmployee] = useState(systemAdminUsers.updatedUser?.employee)
  const [departments, setDepartments] = useState(systemAdminUsers.updatedUser?.departments || [])
  const [title, setTitle] = useState(systemAdminUsers.updatedUser?.title)
  const [name, setName] = useState(systemAdminUsers.updatedUser?.name)
  const [password, setPassword] = useState(systemAdminUsers.updatedUser?.password)
  const [email, setEmail] = useState(systemAdminUsers.updatedUser?.email)
  const [language, setLanguage] = useState(systemAdminUsers.updatedUser?.language || 'ENGLISH')
  const [loaded, setLoaded] = useState(false)
  const [validPassword, setValidPassword] = useState(true)

  const [subscriptionReminder, setSubscriptionReminder] = useState(
    systemAdminUsers.updatedUser?.subscription_reminder || DEFAULT_SUBSCRIPTION_REMINDER,
  )

  const [validReminderMessage, setValidReminderMessage] = useState(true)
  useEffect(() => {
    if (subscriptionReminder?.reminder) {
      setValidReminderMessage(subscriptionReminder?.en_message && subscriptionReminder?.ar_message)
    } else {
      setValidReminderMessage(true)
    }
  }, [subscriptionReminder?.reminder,
    subscriptionReminder?.en_message, subscriptionReminder?.ar_message])

  useEffect(() => {
    dispatch(retrieveSystemAdminUsersCompanies())
    dispatch(retrieveTitles())
    setLoaded(true)
  }, [])

  useEffect(() => {
    const integration_type = company?.tenant?.integration?.type
    if (isNotEmpty(integration_type)) {
      if (integration_type === 'STANDALONE') {
        dispatch(retrieveDepartments({ queries: company?.tenant.id }))
        if (!id) setEmployee(null)
      } else {
        dispatch(getEmployeesWithPagination({
          queries: 0,
          actionQueries: { tenantId: company?.tenant.id },
        }))
        setDepartments(null)
        if (!id) setEmployee(null)
      }
    }

    if (company?.sso_protocol === SAML_KEY) {
      setValidPassword(true)
    }
  }, [company])

  useEffect(() => {
    setCustomerAdmin(systemAdminUsers.updatedUser?.customer_admin)
    setCompany(systemAdminUsers.updatedUser?.company)
    setIntegrationType(systemAdminUsers.updatedUser?.company?.tenant?.integration?.type)
    setDepartments(systemAdminUsers.updatedUser?.departments)
    setTitle(systemAdminUsers.updatedUser?.title)
    setEmployee(systemAdminUsers.updatedUser?.employee)
    setName(systemAdminUsers.updatedUser?.name)
    setEmail(systemAdminUsers.updatedUser?.email)
    setLanguage(systemAdminUsers.updatedUser?.language || 'ENGLISH')
    setSubscriptionReminder(
      systemAdminUsers.updatedUser?.subscription_reminder || DEFAULT_SUBSCRIPTION_REMINDER,
    )
    if (!id) {
      dispatch(updateTenantID({ queries: company?.tenant.id }))
    }
  }, [id, systemAdminUsers?.updatedUser?.id])

  useEffect(() => {
    if (loaded) {
      dispatch(
        updateSelectedSystemAdminUserStore({
          queries: {
            ...systemAdminUsers.updatedUser,
            customer_admin,
            company,
            tenant_id: company?.tenant.id,
            employee,
            departments: isNotEmpty(employee) ? departments : null,
            title: title || null,
            name,
            password,
            email,
            language,
            subscription_reminder: subscriptionReminder?.reminder ? subscriptionReminder : null,
          },
        }),
      )
    }

    if (email && validPassword && name && title && language && validReminderMessage) {
      setValidStepOne(true)
    } else {
      setValidStepOne(false)
    }
  }, [company, departments,
    employee, title, name, password, email, language, customer_admin,
    validReminderMessage, subscriptionReminder])

  const vacancyOwnerConverter = (item) => {
    if (item.code) {
      return `${item.code} - ${item.english_name}`
    }
    return item.english_name
  }

  const handleEmployeeChange = (user) => {
    setEmployee(user)
    if (!isNotEmpty(id) && objNotEmpty(user)) {
      setName(user?.english_name || '')
      setEmail(user?.email || '')
    }
  }

  const handleCompanyChange = (data) => {
    setCompany(data)
    setIntegrationType(data?.tenant?.integration?.type)
    setEmployee(null)
    setName(null)
    setEmail(null)
  }

  const setSubscriptionReminderField = (field) => (value) => {
    setSubscriptionReminder((prev) => ({
      ...(prev || DEFAULT_SUBSCRIPTION_REMINDER),
      [field]: value,
    }))
  }

  return (
    <Container maxWidth='xl'>
      <Grid container justify='space-between'>
        <Grid item xs={12} md={8} lg={9} container sx={styles.section}>
          <Grid
            item
            xs={12}
            sx={styles.sectionContainer}>
            <Box sx={{ display: 'block' }}>
              <Typography sx={styles.sectionTitle} variant='h6'>
                {t?.['Basic Information']}
              </Typography>
              <Typography sx={styles.sectionSubTitle} variant='h6'>
                {t?.['This section defines the main information about the user.']}
              </Typography>
            </Box>
            <Box sx={styles.checkboxesContainer}>
              <OfoqCheckBox
                name={t?.Admin}
                defaultOption={customer_admin}
                refState={setCustomerAdmin}
                customStyle={styles.adminCheckbox}
                />
              {id && (
                <Box sx={styles.paymentReminderContainer}>
                  <PaymentReminderIcon style={styles.paymentReminderIcon} />
                  <OfoqCheckBox
                    name={t?.['Reminder Message']}
                    defaultOption={subscriptionReminder?.reminder}
                    refState={setSubscriptionReminderField('reminder')}
                    customStyle={styles.paymentReminderCheckbox}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Divider
          sx={styles.dividerStyle}
          />

          <Grid container sx={styles.inputsContainerFixSize}>
            <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
              {!id ? (
                <OfoqAutoComplete
                  name={t?.Company}
                  defaultOption={company}
                  refState={handleCompanyChange}
                  optionsConfig={systemAdminUsers?.companiesValue}
                  required
                />
              ) : (
                <OfoqTextField
                  name={t?.Company}
                  refState={handleCompanyChange}
                  defaultValue={systemAdminUsers.updatedUser?.company?.name}
                  type='text'
                  disabled
                />
              )}
            </Grid>

            {integrationType !== 'STANDALONE' && <>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                {isNotEmpty(company) && (
                  <OfoqMultiAutoCompleteWithLoadMore
                  name={t?.Employee}
                  defaultOption={employee}
                  refState={handleEmployeeChange}
                  store={employees?.value}
                  nameConverter={vacancyOwnerConverter}
                  loadMoreActionToDispatch={getEmployeesWithPagination}
                  searchActionToDispatch={searchEmployees}
                  actionQueries={{ tenantId: company?.tenant.id }}
                  multiple={false}
                  noOptionsText ={t?.['Employee not Found']}
                  customPlaceHolder={t?.['an Employee']}
                  customSearchPlaceholder={t?.['Search for Employee by Code or Name']}
                  reduxStore={employees}
                />)}
              </Grid>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqAutoComplete
                  name={t?.Title}
                  defaultOption={title}
                  refState={setTitle}
                  optionsConfig={titles?.value}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Name}
                  placeholder={t?.['Type Employee Name']}
                  defaultValue={name}
                  refState={setName}
                  type='text'
                  required
                  maxLength={255}
                />
              </Grid>
            </>}

            {integrationType === 'STANDALONE' && <>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Employee}
                  placeholder={t?.['Type Employee Code']}
                  defaultValue={employee?.code || employee}
                  refState={setEmployee}
                />
              </Grid>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqAutoComplete
                  name={t?.Title}
                  defaultOption={title}
                  refState={setTitle}
                  optionsConfig={titles?.value}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqAutoComplete
                  name={t?.Manager_of_Department}
                  defaultOption={departments}
                  refState={setDepartments}
                  optionsConfig={department?.value}
                  disabled={!isNotEmpty(employee)}
                  multiple
                />
              </Grid>
              <Grid item xs={12} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Name}
                  placeholder={t?.['Type Employee Name']}
                  defaultValue={name}
                  refState={setName}
                  type='text'
                  required
                  maxLength={255}
                />
              </Grid>
            </>}

            {integrationType && <>
              <Grid item xs={12} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Email}
                  placeholder={t?.['Type the user email']}
                  defaultValue={email}
                  refState={setEmail}
                  type='email'
                  required
                  maxLength={255}
                />
              </Grid>
              {!id && (
                <Grid item xs={12} style={styles.inputsGroupFixSize}>
                  <OfoqPasswordField
                    name={t?.Password}
                    placeholder={t?.['Type the user password']}
                    defaultValue={password}
                    refState={setPassword}
                    setValidPassword={setValidPassword}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12} style={styles.inputsGroupFixSize}>
                 <FormControl fullWidth>
                  <InputLabel htmlFor='language' sx={styles.formInputLabelRequired}>
                    {t?.language} <TeamSnap />
                  </InputLabel>
                </FormControl>
                <RadioGroup style={styles.radioGroup}>
                  <Grid item xs={6} sm={6}>
                    <FormControl fullWidth component='fieldset'>
                      <FormControlLabel
                        required
                        fullWidth
                        control={<Radio sx={styles.radioIcon} value={language} checked={language === 'ENGLISH'} />}
                        value='ENGLISH'
                        name='language'
                        id='language'
                        label={t?.English}
                        variant='outlined'
                        onChange={() => setLanguage('ENGLISH')}
                        sx={
                          language === 'ENGLISH'
                            ? styles.activeRadioContainer
                            : styles.radioContainer
                        }
                        InputProps={{
                          autoComplete: 'new-name',
                          form: {
                            autoComplete: 'off',
                          },
                        }}></FormControlLabel>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <FormControl fullWidth component='fieldset'>
                      <FormControlLabel
                        required
                        fullWidth
                        control={<Radio sx={styles.radioIcon} value={language} checked={language === 'ARABIC'} />}
                        value='ARABIC'
                        name='language'
                        id='language'
                        label={t?.Arabic}
                        variant='outlined'
                        onChange={() => setLanguage('ARABIC')}
                        sx={
                          language === 'ARABIC'
                            ? styles.activeRadioContainer
                            : styles.radioContainer
                        }
                        InputProps={{
                          autoComplete: 'new-name',
                          form: {
                            autoComplete: 'off',
                          },
                        }}></FormControlLabel>
                    </FormControl>
                  </Grid>

                </RadioGroup>
              </Grid>
              {id && subscriptionReminder?.reminder && (
                <Grid item xs={12} sx={styles.reminderSection}>
                  <Box sx={styles.reminderHeader}>
                    <Typography sx={styles.reminderTitle}>
                      <PaymentReminderIcon style={styles.reminderTitleIcon} />
                      {t?.['Reminder Messages']}
                    </Typography>
                    <Typography sx={styles.reminderRequired}>
                      {t?.['Both fields are required']}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <OfoqTextArea
                        name={t?.['Reminder Message (English)']}
                        customPlaceHolder={t?.['Enter the reminder message in English...']}
                        defaultValue={subscriptionReminder?.en_message}
                        refState={setSubscriptionReminderField('en_message')}
                        required
                        customStyle={styles.reminderTextArea}
                      />
                      <Typography sx={styles.reminderHelper}>
                        {t?.['Displayed to English-language users']}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <OfoqTextArea
                        name={t?.['Reminder Message (Arabic)']}
                        customPlaceHolder={t?.['Enter the reminder message in Arabic...']}
                        defaultValue={subscriptionReminder?.ar_message}
                        refState={setSubscriptionReminderField('ar_message')}
                        required
                        customStyle={styles.reminderTextArea}
                      />
                      <Typography sx={styles.reminderHelper}>
                        {t?.['Displayed to Arabic-language users (RTL)']}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
            }
          </Grid>
        </Grid>

        <Grid item md={4} lg={3} sx={styles.imageContainer}>
          <img src={completeInfoImage} alt='complete-profile-art' />
        </Grid>
      </Grid>
    </Container>
  )
}

const styles = {
  section: {
    boxShadow: '0 1px 3px 0 #cfd8dc, 0 0 0 1px #f1f1f1',
    display: 'block',
    padding: { xs: '10px', sm: '20px' },
    borderRadius: 5,
    background: '#fff',
    height: '100%',
  },
  sectionContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'normal' },
    p: '5px 10px',
  },
  sectionTitle: {
    fontFamily: 'Avenir 85 Heavy',
    fontWeight: 500,
    fontSize: { xs: '14px', sm: '20px' },
  },
  sectionSubTitle: {
    width: '100%',
    fontFamily: 'Avenir',
    fontWeight: 500,
    fontSize: { xs: '12px', sm: '16px' },
    lineHeight: 1,
    letterSpacing: 0.1,
    color: '#98A9BC',
    marginTop: '5px',
  },
  dividerStyle: {
    my: { xs: '5px', sm: '10px' },
  },
  formInputLabelRequired: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    color: '#000',
    fontSize: { xs: 12, sm: 16 },
    '& svg': {
      mx: '10px',
    },
  },
  radioGroup: {
    flexDirection: 'row',
    width: '100%',
  },
  radioIcon: {
    '&.Mui-checked .MuiSvgIcon-root': {
      color: '#fff',
    },
  },
  activeRadioContainer: {
    width: '85%',
    height: { xs: '15px', sm: '20px' },
    margin: '4px 14px 16px 8px',
    padding: { xs: '8px', sm: '16px' },
    borderRadius: '8px',
    border: 'solid 2px #e2e8ee',
    backgroundColor: '#29cc8b',
    color: '#ffffff',
    '& .MuiIconButton-label': {
      color: '#ffffff !important',
    },
  },
  radioContainer: {
    width: '85%',
    height: { xs: '15px', sm: '20px' },
    margin: '4px 14px 16px 8px',
    padding: { xs: '8px', sm: '16px' },
    borderRadius: '8px',
    border: 'solid 2px #e2e8ee',
    backgroundColor: '#fafbfc',
  },

  inputsContainerFixSize: {
    display: 'flex',
    width: '100%',
  },

  inputsGroupFixSize: {
    padding: '5px 10px',
  },
  imageContainer: {
    display: { xs: 'none', md: 'block' },
    '& img': {
      my: 'auto',
      maxWidth: '300px',
      height: '600px',
    },
  },
  paymentReminderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    backgroundColor: '#29cc8b30',
    width: { xs: '180px', sm: '200px' },
    height: '20px',
    borderRadius: '10px',
    border: 'solid 1px #29cc8b',
    padding: { xs: '8px 0px', sm: '8px 16px' },
    margin: { xs: '0px', sm: '10px' },
    fontSize: '14px',
    fontWeight: '500',
    color: '#000000',
  },
  adminCheckbox: {
    marginLeft: { xs: '5px', sm: '0px' },
  },
  checkboxesContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: '10px',
    alignItems: { xs: 'flex-start', sm: 'center' },
  },
  paymentReminderCheckbox: {
    width: { xs: '200px', sm: '200px' },
    '.MuiFormControlLabel-label': {
      width: { xs: 'max-content', sm: '200px' },
    },
  },
  paymentReminderIcon: {
    width: '20px',
    height: '20px',
  },
  reminderSection: {
    border: 'solid 1px #29cc8b',
    borderRadius: '12px',
    backgroundColor: '#29cc8b10',
    padding: '20px',
    margin: '5px 10px',
  },
  reminderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  reminderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'Avenir 85 Heavy',
    fontSize: '16px',
    fontWeight: 600,
    color: '#000',
  },
  reminderTitleIcon: {
    width: '22px',
    height: '22px',
  },
  reminderRequired: {
    fontSize: '13px',
    fontStyle: 'italic',
    color: '#29cc8b',
  },
  reminderHelper: {
    fontSize: '12px',
    color: '#98A9BC',
    marginTop: '-16px',
    paddingLeft: '2px',
  },
  reminderTextArea: {
    resize: 'none',
  },
}
export default UserStepOne
